import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
	Tooltip,
	Flex,
	Checkbox,
	Box,
	Button,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	DrawerHeader,
	Table,
	Tbody,
	Td,
	Text,
	Tr,
} from "@chakra-ui/react";
import { userDisplayText } from "@/js/utils/userUtils";
import { UserDetail } from "@/stores/classroomStore_I";
import { colours } from "@/utils/themeUtils";
import { TableContainer } from "@/components/ui/Table";
import { ChakraFAIcon } from "../ui/Icon";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { WarningBanner } from "@/components/WarningBanner";
import { studentClaimsToMapByStudentId, useStudentClaimsQuery } from "@/query/studentClaimsQuery";

interface ScreenshareModalProps {
	usersSelectedCallback: (students: string[]) => Promise<void>;
	endScreenshareCallback: () => void;
	open: boolean;
	close: () => void;
	screenshareOngoing: boolean;
	/**
	 * All students in the classroom
	 */
	allStudents: UserDetail[];
	/**
	 * The students that are currently in the call
	 */
	currentCallStudentUsernames: string[];
	/**
	 * The students that are paused
	 */
	pausedStudentUsernames: string[];
	/**
	 * The students that are focused
	 */
	focusedStudentIds: string[];
	/**
	 * The stable ID of the classroom
	 */
	classroomStableId: string;
}

const ScreenshareModal = ({
	usersSelectedCallback,
	endScreenshareCallback,
	open,
	close,
	allStudents,
	currentCallStudentUsernames,
	screenshareOngoing,
	pausedStudentUsernames,
	focusedStudentIds,
	classroomStableId,
}: ScreenshareModalProps): React.ReactElement => {
	const { t } = useTranslation();
	const tabLimit = useClassroomPolicyStore((state) => state.getTabLimit());
	const students = useMemo(() => [...allStudents], [allStudents]);

	const studentClaimsQuery = useStudentClaimsQuery({ classroomStableId: classroomStableId });
	const studentClaimsMap = useMemo(() => studentClaimsToMapByStudentId(studentClaimsQuery?.data ?? []), [studentClaimsQuery?.data]);

	const [selectedUsers, setSelectedUsers] = useState<string[]>(() => {
		if (!open) return [];
		return students
			.map((u) => u["username"])
			.filter((username) => {
				const user = students.find((u) => u.username === username);
				const isPaused = pausedStudentUsernames.includes(username);
				const isFocused = user && focusedStudentIds.includes(user.stableIdV1);
				const claim = user && studentClaimsMap[user.qoriaUserId];
				let isStudentClaimed = true;
				if (claim) {
					isStudentClaimed = claim.classId === classroomStableId;
				}
				return !isPaused && !isFocused && isStudentClaimed;
			});
	});

	// Filter out paused/focused students from currentCallStudentUsernames
	const filteredInitialUsers = useMemo(() => {
		return currentCallStudentUsernames.filter((username) => {
			const user = students.find((u) => u.username === username);
			const isPaused = pausedStudentUsernames.includes(username);
			const isFocused = user && focusedStudentIds.includes(user.stableIdV1);
			const claim = user && studentClaimsMap[user.qoriaUserId];
			let isStudentClaimed = true;
			if (claim) {
				isStudentClaimed = claim.classId === classroomStableId;
			}
			return !isPaused && !isFocused && isStudentClaimed;
		});
	}, [currentCallStudentUsernames, students, pausedStudentUsernames, focusedStudentIds, classroomStableId, studentClaimsMap]);

	const allSelectedUsers = useMemo(() => [...selectedUsers, ...filteredInitialUsers], [selectedUsers, filteredInitialUsers]);
	const [loading, setLoading] = useState<boolean>(false);
	const [allStudentsSelected, setAllStudentsSelected] = useState<boolean>(() => {
		const isAnyStudentAvailable = students.some((user) => {
			const isPaused = pausedStudentUsernames.includes(user.username);
			const isFocused = focusedStudentIds.includes(user.stableIdV1);
			const claim = user && studentClaimsMap[user.qoriaUserId];
			let isStudentClaimed = true;
			if (claim) {
				isStudentClaimed = claim.classId === classroomStableId;
			}
			return !isPaused && !isFocused && isStudentClaimed;
		});
		return isAnyStudentAvailable;
	});

	useEffect(() => {
		if (!open) {
			setSelectedUsers([]);
			setAllStudentsSelected(false);
		}
	}, [open]);

	const canSubmit = useMemo(() => {
		// Button should be disabled if no new students are selected to add
		return selectedUsers.length > 0;
	}, [selectedUsers]);

	const isScreenshareAvailableForUser = useCallback(
		(user: UserDetail): boolean => {
			// current students are the initial students that are selected, these cannot be deselected
			const isAlreadyScreenSharing = currentCallStudentUsernames.includes(user.username);
			const paused = pausedStudentUsernames.includes(user.username);

			const focused = !!user.stableIdV1 && focusedStudentIds.includes(user.stableIdV1);

			const claim = user && studentClaimsMap[user.qoriaUserId];
			let isStudentClaimed = true;
			if (claim) {
				isStudentClaimed = claim.classId === classroomStableId;
			}
			return !paused && !isAlreadyScreenSharing && !focused && isStudentClaimed;
		},
		[currentCallStudentUsernames, pausedStudentUsernames, focusedStudentIds, classroomStableId, studentClaimsMap]
	);

	const selectAllUsers = useCallback(() => {
		const allUsers = students.filter((u) => isScreenshareAvailableForUser(u));
		const allUsernames = allUsers.map((u) => u.username);
		setSelectedUsers([...allUsernames]);
		setAllStudentsSelected(true);
	}, [students, isScreenshareAvailableForUser]);

	const toggleSelectAllUsers = () => {
		if (allStudentsSelected) {
			setSelectedUsers([]);
			setAllStudentsSelected(false);
		} else {
			selectAllUsers();
		}
	};

	const isAnyStudentAvailable = useMemo(() => {
		return students.some((user) => {
			const isPaused = pausedStudentUsernames.includes(user.username);
			const isFocused = focusedStudentIds.includes(user.stableIdV1);
			const claim = user && studentClaimsMap[user.qoriaUserId];
			let isStudentClaimed = true;
			if (claim) {
				isStudentClaimed = claim.classId === classroomStableId;
			}
			return !isPaused && !isFocused && isStudentClaimed;
		});
	}, [students, pausedStudentUsernames, focusedStudentIds, classroomStableId, studentClaimsMap]);

	const studentItem = (student: UserDetail) => {
		const username: string = student["username"];
		const isAlreadyScreenSharing = currentCallStudentUsernames.includes(username);
		const paused = pausedStudentUsernames.includes(username);
		const focused = focusedStudentIds.includes(student["stableIdV1"]);
		const claim = student && studentClaimsMap[student.qoriaUserId];
		let isStudentClaimed = true;
		if (claim) {
			isStudentClaimed = claim.classId === classroomStableId;
		}
		// Only consider a student checked if they are in selectedUsers and not already screen sharing
		const isChecked = !isAlreadyScreenSharing && allSelectedUsers.includes(username);

		const checkbox = (
			<Checkbox
				data-testid={`screenshare-checkbox-${username}`}
				isChecked={isChecked}
				isDisabled={isAlreadyScreenSharing || paused || focused || !isStudentClaimed}
				onChange={() => {
					if (!isChecked) {
						const newSelectedUsers = [...selectedUsers, username];
						setSelectedUsers(newSelectedUsers);
						// Check if all available students are now selected
						const allAvailableStudents = students.filter((u) => isScreenshareAvailableForUser(u)).map((u) => u.username);
						if (newSelectedUsers.length === allAvailableStudents.length) {
							setAllStudentsSelected(true);
						}
					} else {
						const newSelectedUsers = selectedUsers.filter((u) => u !== username);
						setSelectedUsers(newSelectedUsers);
						setAllStudentsSelected(false);
					}
				}}
			>
				{userDisplayText(student)}
				{!isStudentClaimed && (
					<Tooltip label={t("students.managedByOtherClass")}>
						<ChakraFAIcon icon="fa-question-circle" variant="solid" color={"neutrals.500"} mx={2} />
					</Tooltip>
				)}
			</Checkbox>
		);

		return (
			<Td key={student["id"]} p={4}>
				<Flex p={0} flexDirection={"row"}>
					<Box alignSelf="flex-start">
						{paused || focused ? (
							<Tooltip placement="top" label={t("screenshare.tooltipPausedFocused")}>
								<Box>{checkbox}</Box>
							</Tooltip>
						) : (
							checkbox
						)}
					</Box>
					<Flex ml={"auto"} gap={2} alignItems="center" justifyContent="flex-end">
						{isAlreadyScreenSharing && !paused && !focused && (
							<ChakraFAIcon icon="fa-screencast" variant="solid" boxSize={5} color="var(--primary-color)" />
						)}
						{isAlreadyScreenSharing && (paused || focused) && (
							<ChakraFAIcon icon="fa-screencast" variant="solid" boxSize={5} opacity={0.5} />
						)}
						{!isAlreadyScreenSharing && paused && <ChakraFAIcon icon="fa-pause" variant="solid" boxSize={5} opacity={0.5} />}
						{!isAlreadyScreenSharing && focused && <ChakraFAIcon icon="fa-arrows-to-eye" variant="solid" boxSize={5} opacity={0.5} />}
					</Flex>
				</Flex>
			</Td>
		);
	};

	const tableRows = () => {
		const rows = [];
		for (let row = 0; row < students.length; row += 2) {
			rows.push(
				<Tr key={`tr_${row}`}>
					{studentItem(students[row])}
					<>{students.length > row + 1 && studentItem(students[row + 1])}</>
				</Tr>
			);
		}
		return rows;
	};

	return (
		<Drawer isOpen={open} onClose={close} size={"md"}>
			<DrawerOverlay />
			<DrawerContent>
				<Flex px={2} py={4} bgColor={colours.gray[100]} alignItems="center" justifyContent="space-between">
					<DrawerHeader fontSize="18px" fontWeight="500" whiteSpace={"nowrap"}>
						{t("screenshare.modalHeader")}
					</DrawerHeader>
					<DrawerCloseButton color={colours.neutrals[300]} position="absolute" top={3} right={3} />
				</Flex>
				{tabLimit !== null && (
					<Box px={6} pt={4}>
						<WarningBanner message={t("screenshare.tabLimitWarning", { tabLimit })} />
					</Box>
				)}
				<Flex h="80vh" flex="1" flexDirection="column" justifyContent="flex-start">
					<Box mt={6} ml={6} pb={6}>
						<Checkbox isChecked={allStudentsSelected} onChange={toggleSelectAllUsers} isDisabled={!isAnyStudentAvailable}>
							<Text fontFamily="Roboto" fontSize="14px" fontWeight="500">
								{t("screenshare.selectAllStudents")}
							</Text>
						</Checkbox>
					</Box>
					<Flex pb={6} px={6} overflowY="auto">
						<TableContainer>
							<Table variant="qoriaStriped" width="100%">
								<Tbody>{tableRows()}</Tbody>
							</Table>
						</TableContainer>
					</Flex>
				</Flex>
				<Flex flexDirection={"row"} justifyContent={"flex-end"} justifySelf="end">
					<Flex w="100%" h="72px" px="20px" bgColor={colours.gray[100]} alignItems="center" justifyContent="space-between">
						{screenshareOngoing ? (
							<Button
								variant={"danger"}
								onClick={() => {
									endScreenshareCallback();
								}}
							>
								{t("screenshare.stopSharing")}
							</Button>
						) : (
							<Flex></Flex>
						)}
						<Button
							isDisabled={!canSubmit}
							onClick={() => {
								setLoading(true);
								usersSelectedCallback(allSelectedUsers)
									.then(() => {
										setLoading(false);
									})
									.catch((error) => {
										console.error(`Error selecting students`, error);
										setLoading(false);
									});
							}}
							isLoading={loading}
							rightIcon={<ChakraFAIcon icon="fa-arrow-right" variant="solid" />}
						>
							{screenshareOngoing ? t("screenshare.addToShare") : t("screenshare.selectWhatToShare")}
						</Button>
					</Flex>
				</Flex>
			</DrawerContent>
		</Drawer>
	);
};

export default ScreenshareModal;
